import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/70' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 no-underline">
          <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
            Kakobuy<span className="text-primary-600">Polska</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/"
            className="px-3 py-2 rounded-full text-sm font-label font-medium text-foreground-700 hover:text-primary-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line mr-1"></i>
            Strona główna
          </Link>

          <Link
            to="/artykul/kakobuy-dla-poczatkujacych"
            className="px-3 py-2 rounded-full text-sm font-label font-medium text-foreground-700 hover:text-primary-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-book-open-line mr-1"></i>
            Poradnik zakupowy
          </Link>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-label font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-apps-2-line"></i>
              Kategorie
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-sm py-2 max-h-[60vh] overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700 transition-colors cursor-pointer"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100">
                      <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                    </span>
                    <div className="flex flex-col">
                      <span className="font-medium">{cat.name}</span>
                      <span className="text-xs text-foreground-400">{cat.nameEn}</span>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 font-label font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-cart-2-line"></i>
            Buy It Now
          </a>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 font-label font-semibold text-xs hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Buy It Now
          </a>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
          >
            <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="md:hidden bg-background-50 border-t border-background-200/70 shadow-sm max-h-[80vh] overflow-y-auto"
        >
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-label font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-lg"></i>
              Strona główna
            </Link>
            <Link
              to="/artykul/kakobuy-dla-poczatkujacych"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-label font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line text-lg"></i>
              Poradnik zakupowy
            </Link>
            <Link
              to="/artykul/czy-kakobuy-jest-bezpieczny"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-label font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-shield-check-line text-lg"></i>
              Bezpieczeństwo
            </Link>
            <Link
              to="/artykul/najlepsze-kakobuy-finds"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-label font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-search-eye-line text-lg"></i>
              Najlepsze znaleziska
            </Link>
          </div>

          <div className="px-4 py-2 border-t border-background-200/70">
            <p className="px-3 py-2 text-xs font-label font-semibold text-foreground-400 uppercase tracking-wider">
              Kategorie produktów
            </p>
          </div>

          <div className="px-4 pb-4 space-y-0.5">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100">
                  <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                </span>
                <span className="font-medium">{cat.name}</span>
                <span className="ml-auto text-xs text-foreground-400">{cat.nameEn}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}